import { Helmet } from 'react-helmet-async';
import BottomNav from '../../../components/layouts/Footer/BottomNav';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// You might have a global styles file, but for this component, we'll keep styles here for clarity.

const ReturnPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {/* SEO Metadata for Google */}
      <Helmet>
        <title>Return Policy - BigLocal.in</title>
        <meta
          name="description"
          content="Read the official return and refund policy for BigLocal.in. Learn about conditions for returns on damaged or incorrect items, processing times, and refund methods."
        />
        <link rel="canonical" href="https://www.biglocal.in/return-policy" />
      </Helmet>
      <div
        style={{
          maxWidth: '1200px',
          margin: '0rem auto',
          padding: '2.5rem 3rem',
          backgroundColor: '#ffffff', // White background for the content card
          borderRadius: '12px', // Softer rounded corners
          fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", // Modern, clean font stack
          color: '#333', // Darker text for readability
          lineHeight: '1.7', // Improved line spacing
          textAlign: 'left', // Explicitly left-aligned text for readability, though the block is centered
        }}
      >
        {/* Header Section */}
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <h1 className="text-3xl flex items-center gap-3 justify-center sm:text-4xl font-extrabold text-center mb-8 tracking-tight text-gray-900 dark:text-gray-100">
            <ArrowLeft onClick={() => navigate(-1)} className="cursor-pointer size-8" />
            Our Return Policy
          </h1>

          <div
            style={{
              width: '80px',
              height: '4px',
              backgroundColor: '#007bff', // A strong accent color
              margin: '1.5rem auto 0 auto', // Centered divider
              borderRadius: '2px',
            }}
          ></div>
        </div>

        {/* General Policy Section */}
        <section style={{ marginBottom: '1rem' }}>
          <h2
            style={{
              fontSize: '1.2rem',
              color: '#007bff', // Highlight section titles
              marginBottom: '1rem',
              fontWeight: '600',
            }}
          >
            General Guidelines
          </h2>
          <p style={{ marginBottom: '1.5rem' }}>
            At BigLocal.in, we strive to ensure every purchase meets your expectations. However, due
            to the nature of our products and vendor policies, items are generally{' '}
            <strong>non-returnable, non-replaceable, and non-exchangeable</strong>. Your
            satisfaction remains our priority, and we do make exceptions under specific, outlined
            circumstances.
          </p>
        </section>

        {/* Conditions for Returns Section */}
        <section style={{ marginBottom: '2.5rem' }}>
          <h2
            style={{
              fontSize: '1.2rem',
              color: '#007bff',
              marginBottom: '1rem',
              fontWeight: '600',
            }}
          >
            Conditions for Eligibility
          </h2>
          <p style={{ marginBottom: '1.5rem' }}>
            You may be eligible to request a return or replacement if the product received falls
            into one of the following categories:
          </p>
          <ul
            style={{
              listStyleType: 'disc',
              paddingLeft: '25px', // Indent list items
              margin: '0',
            }}
          >
            <li style={{ marginBottom: '0.8rem' }}>
              <strong>Damaged Goods:</strong> The product exhibits physical damage upon delivery.
            </li>
            <li style={{ marginBottom: '0.8rem' }}>
              <strong>Defective Items:</strong> The product is found to be defective or not
              functioning as intended.
            </li>
            <li style={{ marginBottom: '0.8rem' }}>
              <strong>Expired Products:</strong> The product has passed its stated expiry date when
              delivered to you.
            </li>
            <li style={{ marginBottom: '0.8rem' }}>
              <strong>Incorrect Deliveries:</strong> The product received is not the item you
              ordered.
            </li>
          </ul>
          <p style={{ marginTop: '1.5rem', fontSize: '0.95rem', color: '#555' }}>
            <em>
              Please refer to the individual product page for any specific return policies set forth
              by the vendor.
            </em>
          </p>
        </section>

        {/* How to Initiate a Return Section */}
        <section style={{ marginBottom: '2.5rem' }}>
          <h2
            style={{
              fontSize: '1.2rem',
              color: '#007bff',
              marginBottom: '1rem',
              fontWeight: '600',
            }}
          >
            Initiating a Return Request
          </h2>
          <p>
            If your item qualifies for a return based on the above conditions, please follow these
            steps:
          </p>
          <ol
            style={{
              listStyleType: 'decimal',
              paddingLeft: '25px',
              margin: '1.5rem 0',
            }}
          >
            <li style={{ marginBottom: '0.8rem' }}>
              <strong>Contact Support:</strong> Reach out to our customer support team within the
              specified timeframe (usually within 48 hours of delivery, or as mentioned on the
              product page).
            </li>
            <li style={{ marginBottom: '0.8rem' }}>
              <strong>Provide Details:</strong> Have your Order ID ready and provide clear
              photographic or video evidence of the product&apoos;s condition or discrepancy.
            </li>
            <li style={{ marginBottom: '0.8rem' }}>
              <strong>Await Approval:</strong> Our team, in conjunction with the vendor, will review
              your request and communicate the next steps.
            </li>
          </ol>
        </section>

        {/* Refund Process Section */}
        <section style={{ marginBottom: '2.5rem' }}>
          <h2
            style={{
              fontSize: '1.2rem',
              color: '#007bff',
              marginBottom: '1rem',
              fontWeight: '600',
            }}
          >
            Refund Process for Approved Returns
          </h2>
          <p style={{ marginBottom: '1.5rem' }}>
            Upon approval of your return request by the vendor, we will proceed with the refund
            according to the following guidelines:
          </p>
          <ul
            style={{
              listStyleType: 'circle',
              paddingLeft: '25px',
              margin: '0',
            }}
          >
            <li style={{ marginBottom: '0.8rem' }}>
              <strong>Processing Timeline:</strong> Refunds are typically processed within{' '}
              <strong>7 business days</strong> from the date of the return approval. This period may
              vary based on individual vendor terms.
            </li>
            <li style={{ marginBottom: '0.8rem' }}>
              <strong>Currency:</strong> All refunds are processed and issued in{' '}
              <strong>Indian Rupees (INR)</strong>.
            </li>
          </ul>
        </section>

        {/* Important Note */}
        <div
          style={{
            marginTop: '3rem',
            padding: '1.5rem',
            backgroundColor: '#f8f9fa', // Light grey background for the note
            borderRadius: '8px',
            borderLeft: '5px solid #ffc107', // Warning-like accent border
          }}
        >
          <h3
            style={{
              fontSize: '1.4rem',
              color: '#dc3545', // Red color for emphasis
              marginBottom: '0.8rem',
              fontWeight: '600',
            }}
          >
            Important Note:
          </h3>
          <p style={{ fontSize: '0.95rem', color: '#555' }}>
            BigLocal.in reserves the right to make final decisions on all return and refund
            requests. Our goal is to provide fair and transparent resolutions while adhering to
            vendor policies.
          </p>
        </div>
      </div>
      <BottomNav />
      {/* End of Page Content Container */}
    </>
  );
};

export default ReturnPolicy;
